<!-- FUTURE To consider adding:
  - Scroll depth tracking (scroll depth tracking is a feature that tracks how far users have scrolled on a page)
  - Views per visit computation (views per visit is the number of views per unique session)
  - Top Entry Pages aggregation
  - Track special routes (e.g. /login, /all, /messages, /calendar, /posters …)
-->

<script>
  import t, { LANG } from '#lib/i18n.svelte.js';
  import { format_date } from '#lib/util.js';

  /**
   * Analytics data for a specific entry or site-wide
   * @type {{ entry_analytics: Object, site_analytics: Object }}
   */
  let {
    entry_analytics,
    site_analytics
  } = $props();

  /** @type {'entry' | 'site'} */
  let active_tab = $state('entry');

  const REFERRER_TYPES = ['direct', 'internal', 'external', 'search', 'ai', 'social'];
  const number_format = new Intl.NumberFormat(LANG);
  const percent_format = new Intl.NumberFormat(LANG, { style: 'percent' });

  /** "62 %" with a narrow space, so the sign stays with its number */
  function format_percent(fraction) {
    return percent_format.format(fraction).replace('\u00a0', '\u202f');
  }

  function largest(breakdown, count, size_of = value => value) {
    return Object.entries(breakdown || {})
      .sort((a, b) => size_of(b[1]) - size_of(a[1]))
      .slice(0, count);
  }

  function source_label(source) {
    return REFERRER_TYPES.includes(source) ? t('referrer_' + source) : source;
  }

  function exit_label(target) {
    if (target === 'tel') return t('phone');
    if (target === 'mail') return t('analytics_exit_mail');
    return target;
  }

  function trend_of(percent) {
    if (percent === null || percent === undefined) return { text: '–', color: 'var(--text-3)' };
    const symbol = percent > 5 ? '↑' : percent < -5 ? '↓' : '→';
    const color = percent > 5 ? 'var(--color-positive)' : percent < -5 ? 'var(--color-destructive)' : 'var(--text-2)';
    return { text: `${symbol} ${format_percent(Math.abs(percent) / 100)}`, color };
  }

  const SPARKLINE_HEIGHT = 28;

  /** One bar per day; today is still running and drawn lighter */
  function bars_of(views, dates) {
    const max = Math.max(1, ...views);
    return views.map((count, i) => ({
      height: Math.max(1, Math.round((count / max) * SPARKLINE_HEIGHT)),
      label: `${format_date(dates?.[i], 'EEEdMMM', undefined, 'UTC')}: ${number_format.format(count)} ${t('views')}`,
      is_today: i === views.length - 1
    }));
  }
  const analytics = $derived(active_tab === 'entry' ? entry_analytics : site_analytics);
  const has_error = $derived(!analytics || analytics.error);
  const trend = $derived(trend_of(analytics?.trend_percent));
  const bars = $derived(bars_of(analytics?.sparkline_data || [], analytics?.sparkline_dates));
  const sources = $derived(
    analytics?.total_views > 0
      ? largest(analytics.source_breakdown, 4).map(([source, count]) => ({
          name: source_label(source),
          value: format_percent(count / analytics.total_views)
        }))
      : []
  );
  const campaigns = $derived(
    largest(analytics?.campaigns, 3, campaign => campaign.sessions).map(([name, campaign]) => ({
      name,
      value: number_format.format(campaign.sessions),
      exits: largest(campaign.exits, 2).map(([target, count]) => ({ name: exit_label(target), value: number_format.format(count) }))
    }))
  );
  const exits = $derived(
    largest(analytics?.exits, 3).map(([target, count]) => ({ name: exit_label(target), value: number_format.format(count) }))
  );

  /**
   * One table for all lists: the group label sits on a group's first row, a
   * campaign's exits follow it as indented rows, numbers share one column.
   */
  const rows = $derived.by(() => {
    const rows = [];
    const add_group = (label, items) => items.forEach((item, i) => rows.push({
      ...item,
      label: i === 0 ? label : '',
      starts_group: i === 0 && rows.length > 0
    }));
    add_group(t('analytics_sources'), sources.length > 0 ? sources : [{ name: t('no_data'), value: '' }]);
    add_group(t('analytics_campaigns'), campaigns.flatMap((campaign, i) => [
      { name: campaign.name, value: campaign.value, starts_item: i > 0 },
      ...campaign.exits.map(exit => ({ ...exit, is_exit: true }))
    ]));
    add_group(t('analytics_exits'), exits);
    return rows;
  });
</script>

<!-- A campaign name that doesn't fit wraps after "campaign/" rather than inside a word -->
{#snippet name_with_breaks(name)}{#each name.split('/') as part, i}{#if i > 0}/<wbr>{/if}{part}{/each}{/snippet}

<div class="analytics px p-5">
  <div class="header">
    <div class="tabs" role="tablist">
      <button
        class="tab"
        role="tab"
        aria-selected={active_tab === 'entry'}
        onclick={() => active_tab = 'entry'}
      >
        {t('this_page')}
      </button>
      <button
        class="tab"
        role="tab"
        aria-selected={active_tab === 'site'}
        onclick={() => active_tab = 'site'}
      >
        {t('site_total')}
      </button>
    </div>
    {#if !has_error}
      <span class="label">{t('analytics_n_days', { days: analytics.days })}</span>
    {/if}
  </div>

  {#if has_error}
    <p class="label">{t('no_data')}</p>
  {:else}
    <div class="figures">
      <div>
        <span class="figure">{number_format.format(analytics.total_views || 0)}</span>
        <span class="label">{t('views')}</span>
      </div>
      <div>
        <span class="figure">{number_format.format(analytics.total_unique || 0)}</span>
        <span class="label">{t('analytics_visits')}</span>
      </div>
      <div>
        <span class="figure" style:color={trend.color}>{trend.text}</span>
        <span class="label">{t('analytics_trend')}</span>
      </div>
    </div>

    <svg
      class="sparkline"
      viewBox="0 0 {bars.length * 10 - 2} {SPARKLINE_HEIGHT}"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {#each bars as bar, i}
        <rect
          x={i * 10}
          y={SPARKLINE_HEIGHT - bar.height}
          width="8"
          height={bar.height}
          class:today={bar.is_today}
        ><title>{bar.label}</title></rect>
      {/each}
    </svg>

    <div class="lists">
      {#each rows as row}
        <span class="label" class:group-start={row.starts_group} class:item-start={row.starts_item}>{row.label}</span>
        <span
          class="name"
          class:exit={row.is_exit}
          class:no-value={!row.value}
          class:group-start={row.starts_group}
          class:item-start={row.starts_item}
        >{#if row.is_exit}<span class="arrow" aria-hidden="true">→</span>{/if}{@render name_with_breaks(row.name)}</span>
        {#if row.value}
          <span
            class="number"
            class:exit={row.is_exit}
            class:group-start={row.starts_group}
            class:item-start={row.starts_item}
          >{row.value}</span>
        {/if}
      {/each}
    </div>
  {/if}
</div>

<style>
  /* Plain UI type, independent of the site's typography: a site's text styles
     (letter case, letter spacing, display fonts) make small numbers unreadable */
  .analytics {
    display: flex;
    flex-direction: column;
    gap: var(--size-3);
    min-width: 18rem; /* the menu keeps its width; other items are narrower */
    font-family: var(--font-sans-fallback);
    font-size: 0.8125rem;
    font-weight: 400;
    font-style: normal;
    line-height: 1.45;
    letter-spacing: normal;
    word-spacing: normal;
    text-transform: none;
    font-feature-settings: normal;
    font-variation-settings: normal;
    color: var(--text-2);
    user-select: text;
  }
  .analytics :is(button, p) {
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    margin: 0;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--size-3);
  }
  .tabs {
    display: flex;
    gap: var(--size-4);
  }
  .tab {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    padding: 0;
    color: var(--text-3);
  }
  /* Both states drawn the same size: the ● and ○ glyphs differ in size and weight */
  .tab::before {
    content: '';
    box-sizing: border-box;
    width: 0.55em;
    height: 0.55em;
    border: 1px solid currentColor;
    border-radius: 50%;
  }
  .tab[aria-selected='true'] {
    color: var(--text-1);
  }
  .tab[aria-selected='true']::before {
    background: currentColor;
  }
  .tab:hover {
    color: var(--text-1);
  }

  .label {
    color: var(--text-3);
  }
  .number,
  .figure {
    color: var(--text-1);
    font-variant-numeric: tabular-nums;
  }

  /* The lists' name column starts where the second figure does */
  .figures,
  .lists {
    --column-gap: var(--size-3);
    column-gap: var(--column-gap);
  }
  .figures {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .figures > div {
    display: flex;
    flex-direction: column;
  }
  .figure {
    font-size: 0.9375rem;
  }

  /* width 0 + min-width 100%: neither the chart (an svg is 300px wide by
     default) nor the lists widen the menu; long names wrap */
  .sparkline {
    display: block;
    width: 0;
    min-width: 100%;
    height: 28px;
    fill: var(--text-3);
  }
  .sparkline .today {
    opacity: 0.5;
  }

  .lists {
    display: grid;
    grid-template-columns:
      minmax(max-content, calc((100% - 2 * var(--column-gap)) / 3))
      minmax(0, 1fr)
      max-content;
    width: 0;
    min-width: 100%;
  }
  .lists .name {
    overflow-wrap: anywhere;
  }
  .lists .number {
    text-align: end;
  }
  .lists .no-value {
    grid-column: 2 / -1;
    color: var(--text-3);
  }
  /* A campaign's exits: one step lighter, wrapped lines hang after the arrow */
  .lists .exit {
    color: var(--text-3);
  }
  .lists .name.exit {
    padding-inline-start: 1.2em;
    text-indent: -1.2em;
  }
  .arrow {
    display: inline-block;
    width: 1.2em;
    text-indent: 0;
  }
  .lists .number.exit {
    color: var(--text-2);
  }
  .lists .group-start {
    margin-block-start: var(--size-3);
  }
  .lists .item-start {
    margin-block-start: var(--size-2);
  }
</style>
